'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import { NewsInsight } from '@/lib/news';
import { useAudioPlayerContext } from '@/contexts/AudioPlayerContext';

export interface NewsCardProps extends NewsInsight {
  date?: string;
}

export default function NewsCard({
  slug,
  title,
  summary,
  image_url,
  source,
  date,
  published_at,
  image,
}: NewsCardProps) {
  const [imageError, setImageError] = useState(false);
  const { isPlaying, isPaused, currentText, audioMode, toggle } = useAudioPlayerContext();
  
  // Get the API base URL for backend images
  const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
  
  // Use enhanced image metadata if available, fallback to image_url
  const imageUrl = image?.url || image_url;
  const imageSource = image?.source;
  const imageStatus = image?.status;
  
  // Build full image URL - backend serves images at /images/ path
  // Placeholder images are served from frontend public folder
  const PLACEHOLDER_PATH = '/images/placeholder-news.png';
  
  const getImageUrl = (url: string | null | undefined): string => {
    if (!url || imageError) {
      // Placeholder is in frontend public folder, not backend
      return PLACEHOLDER_PATH;
    }
    
    // Skip S3 URLs (legacy)
    if (url.includes('s3.amazonaws.com')) {
      return PLACEHOLDER_PATH;
    }
    
    // Placeholder path should always be served from frontend
    if (url === PLACEHOLDER_PATH || url.includes('placeholder-news')) {
      return PLACEHOLDER_PATH;
    }
    
    // If URL starts with /images/, it's a backend image - prepend API base URL
    // But exclude placeholder paths
    if (url.startsWith('/images/') && !url.includes('placeholder')) {
      return `${API_BASE_URL}${url}`;
    }
    
    // If it's already a full URL, use it
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Default: treat as relative path from API
    return `${API_BASE_URL}${url.startsWith('/') ? url : '/' + url}`;
  };
  
  const displayImage = getImageUrl(imageUrl);
  const articleUrl = date ? `/news/${slug}?date=${date}` : `/news/${slug}`;
  
  // Format the date for display - use published_at if available, otherwise use date
  const displayDate = published_at || date;
  const formattedDate = displayDate 
    ? new Date(displayDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;

  // Use summary text from storage/news/ data
  const cardText = summary;
  const isCardPlaying = isPlaying && currentText === cardText && audioMode === 'card';
  const isCardPaused = isPaused && currentText === cardText && audioMode === 'card';

  const handleAudioClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (cardText) {
      // Add personalized greeting for card audio
      const customGreeting = "I see you're interested in this news. I will read this update for you.";
      toggle(cardText, { 
        mode: 'card', 
        withGreeting: true,
        customGreeting: customGreeting,
        preprocessNumbers: true 
      });
    }
  };

  return (
    <article className="news-card">
      <Link href={articleUrl} className="news-card__link">
        <div className="news-card__image-wrapper">
          <Image
            src={displayImage}
            alt={title}
            fill
            className="news-card__image"
            onError={() => setImageError(true)}
            loading="lazy"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            priority={false}
          />
        </div>
        <div className="news-card__content">
          <h2 className="news-card__title">{title}</h2>
          <p className="news-card__summary">{summary}</p>
          <div className="news-card__footer">
            <div className="news-card__meta">
              <p className="news-card__source">{source}</p>
              {formattedDate && (
                <time className="news-card__date" dateTime={displayDate}>
                  {formattedDate}
                </time>
              )}
            </div>
            {cardText && (
              <button
                className="news-card__audio-button"
                onClick={handleAudioClick}
                aria-label={
                  isCardPlaying ? 'Pause audio' : 
                  isCardPaused ? 'Resume audio' : 
                  'Play audio'
                }
                title={
                  isCardPlaying ? 'Pause audio' : 
                  isCardPaused ? 'Resume audio' : 
                  'Listen to this news'
                }
              >
                {isCardPlaying ? (
                  <>
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                      <path d="M5.5 3.5A.5.5 0 0 1 6 4v8a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5zm5 0A.5.5 0 0 1 11 4v8a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5z"/>
                    </svg>
                    <span>Pause</span>
                  </>
                ) : isCardPaused ? (
                  <>
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                      <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
                    </svg>
                    <span>Resume</span>
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                      <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
                    </svg>
                    <span>Listen</span>
                  </>
                )}
              </button>
            )}
          </div>
          <span className="news-card__cta">Read full news →</span>
        </div>
      </Link>
    </article>
  );
}

